'use client';
import { createContext, useContext, useEffect, useState } from 'react';

type EventsType = {
	lastMinuteTick: Date | null;
	lastTenMinuteTick: Date | null;
	isTabFocused: boolean;
};

const Events = createContext<EventsType>({
	lastMinuteTick: null,
	lastTenMinuteTick: null,
	isTabFocused: true,
});

export function EventsProvider({ children }: { children: React.ReactNode }) {
	const [lastMinuteTick, setOnMinuteUpdate] = useState<Date | null>(null);
	const [lastTenMinuteTick, setOnTenMinutesUpdate] = useState<Date | null>(null);
	const [isTabFocused, setIsTabFocused] = useState<boolean>(true);

	useEffect(() => {
		const worker = new Worker(new URL('./timer-worker.js', import.meta.url));

		worker.onmessage = (e) => {
			if (e.data.type === 'lastMinuteTick') {
				setOnMinuteUpdate(new Date(e.data.now));
				window.dispatchEvent(new CustomEvent('lastMinuteTick', { detail: e.data.now }));
			}

			if (e.data.type === 'lastTenMinuteTick') {
				setOnTenMinutesUpdate(new Date(e.data.now));
				window.dispatchEvent(new CustomEvent('lastTenMinuteTick', { detail: e.data.now }));
			}
		};

		const notifyFocus = () => {
			setIsTabFocused(document.hasFocus());
			worker.postMessage({ type: 'focus', focused: document.hasFocus() });
		};
		window.addEventListener('focus', notifyFocus);
		window.addEventListener('blur', notifyFocus);
		notifyFocus();

		return () => {
			worker.terminate();
			window.removeEventListener('focus', notifyFocus);
			window.removeEventListener('blur', notifyFocus);
		};
	}, []);

	return <Events.Provider value={{ lastMinuteTick, lastTenMinuteTick, isTabFocused }}>{children}</Events.Provider>;
}

export function useEvents() {
	return useContext(Events);
}
